<template>
  <div class="system-config">
    <el-card shadow="never" v-loading="loading">
      <div slot="header" class="card-header">
        <span>系统配置</span>
        <div>
          <el-button size="small" icon="el-icon-refresh" @click="getList">刷新</el-button>
          <el-button type="primary" size="small" icon="el-icon-check" :loading="saving" @click="handleSaveAll">保存全部</el-button>
        </div>
      </div>

      <template v-if="groups.length > 0">
        <el-collapse v-model="activeGroups">
          <el-collapse-item v-for="group in groups" :key="group.group" :name="group.group">
            <template slot="title">
              <span class="group-label">{{ group.label || group.group }}</span>
              <span class="group-count">（{{ group.items.length }} 项）</span>
            </template>

            <el-table :data="group.items" border stripe size="small" style="width: 100%">
              <el-table-column prop="config_name" label="配置名称" width="220">
                <template slot-scope="scope">
                  <span>{{ scope.row.config_name }}</span>
                  <el-tag v-if="scope.row.config_type" size="mini" type="info" class="type-tag">
                    {{ scope.row.config_type }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="config_key" label="配置键" width="240">
                <template slot-scope="scope">
                  <code class="key-code">{{ scope.row.config_key }}</code>
                </template>
              </el-table-column>
              <el-table-column label="配置值" min-width="420">
                <template slot-scope="scope">
                  <reserve-receivers-editor
                    v-if="scope.row.config_key === 'profit_sharing_reserve_receivers'"
                    v-model="scope.row.config_value"
                    :reserve-rate="reserveRateOf(scope.row.config_group)"
                  />
                  <el-input
                    v-else-if="scope.row.config_type === 'json'"
                    v-model="scope.row.config_value"
                    type="textarea"
                    :rows="2"
                    placeholder="JSON 字符串"
                  />
                  <el-input
                    v-else
                    v-model="scope.row.config_value"
                    :type="scope.row.config_type === 'int' ? 'text' : 'text'"
                    placeholder="配置值"
                  />
                </template>
              </el-table-column>
              <el-table-column prop="remark" label="备注" min-width="200">
                <template slot-scope="scope">
                  <span class="remark-text">{{ scope.row.remark }}</span>
                </template>
              </el-table-column>
            </el-table>
          </el-collapse-item>
        </el-collapse>
      </template>

      <el-empty v-else description="暂无配置数据" />
    </el-card>
  </div>
</template>

<script>
import { getSystemConfigs, saveSystemConfigs } from '@/api'
import ReserveReceiversEditor from './ReserveReceiversEditor'

export default {
  name: 'SystemConfig',
  components: { ReserveReceiversEditor },
  data() {
    return {
      loading: false,
      saving: false,
      groups: [],
      activeGroups: []
    }
  },
  created() {
    this.getList()
  },
  methods: {
    getList() {
      this.loading = true
      getSystemConfigs().then(res => {
        this.groups = res || []
        // 默认展开所有分组
        this.activeGroups = this.groups.map(g => g.group)
      }).catch(() => {
        this.$message.error('加载配置失败')
      }).finally(() => { this.loading = false })
    },
    // 获取某分组下"预留奖励比例（总额）"的配置值
    reserveRateOf(groupKey) {
      const group = this.groups.find(g => g.group === groupKey)
      if (!group) return '0.01'
      const rateItem = group.items.find(i => i.config_key === 'commission_reserve_rate')
      return rateItem ? (rateItem.config_value || '0.01') : '0.01'
    },
    handleSaveAll() {
      // 校验 JSON 类型的配置值
      for (const group of this.groups) {
        for (const item of group.items) {
          if (item.config_key === 'profit_sharing_reserve_receivers') {
            // 预留奖励分配账户：校验 JSON 及百分比之和
            if (!this.validateReserveReceivers(group, item)) {
              return
            }
            continue
          }
          if (item.config_type === 'json') {
            try {
              JSON.parse(item.config_value)
            } catch (e) {
              this.$message.error(`配置 "${item.config_name}" 的 JSON 值格式不正确`)
              return
            }
          }
        }
      }

      const items = []
      for (const group of this.groups) {
        for (const item of group.items) {
          items.push({
            config_key: item.config_key,
            config_value: item.config_value
          })
        }
      }

      if (items.length === 0) {
        this.$message.warning('没有可保存的配置')
        return
      }

      this.saving = true
      saveSystemConfigs({ items }).then(() => {
        this.$message.success('配置已保存')
      }).catch(err => {
        this.$message.error(err.message || '保存失败')
      }).finally(() => { this.saving = false })
    },
    // 校验预留奖励分配账户：JSON 格式、有效账户商户号必填、百分比之和等于总额比例
    validateReserveReceivers(group, item) {
      let arr
      try {
        arr = JSON.parse(item.config_value || '[]')
      } catch (e) {
        this.$message.error(`配置 "${item.config_name}" 的 JSON 值格式不正确`)
        return false
      }
      if (!Array.isArray(arr)) {
        this.$message.error(`配置 "${item.config_name}" 的值必须是账户数组`)
        return false
      }
      // 过滤掉完全为空的行（既无商户号也无商户名称），视为未填写的占位行
      const validRows = arr.filter(r =>
        (r.merchant_no && String(r.merchant_no).trim()) ||
        (r.merchant_name && String(r.merchant_name).trim())
      )
      // 全部为空：未配置，允许保存（分账时预留奖励归入总后台）
      if (validRows.length === 0) {
        return true
      }
      // 有效账户：商户号必填
      for (const r of validRows) {
        if (!r.merchant_no || !String(r.merchant_no).trim()) {
          this.$message.error('预留奖励分配账户：存在已填写但未填写微信商户号的账户，请填写或删除')
          return false
        }
        if (r.percent === undefined || r.percent === null || isNaN(parseFloat(r.percent))) {
          this.$message.error('预留奖励分配账户：存在未填写百分比的账户，请填写或删除')
          return false
        }
      }
      // 存储的 percent 为小数（如 0.01 = 1%），与总额比例（commission_reserve_rate，同为小数）单位一致：
      // 各账户小数之和 == 总额比例
      const totalPercent = validRows.reduce((sum, r) => sum + (parseFloat(r.percent) || 0), 0)
      const reserveRate = parseFloat(this.reserveRateOf(group.group))
      if (!isNaN(reserveRate) && Math.abs(totalPercent - reserveRate) >= 0.0001) {
        this.$message.error(`预留奖励分配账户：各账户占比合计（${Math.round(totalPercent * 10000) / 10000}）不等于预留奖励总额比例（${reserveRate}），请调整`)
        return false
      }
      return true
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.group-label {
  font-weight: bold;
  font-size: 15px;
}
.group-count {
  color: #909399;
  font-size: 13px;
  margin-left: 8px;
}
.type-tag {
  margin-left: 6px;
}
.key-code {
  background: #f5f7fa;
  border-radius: 3px;
  padding: 2px 6px;
  color: #E4393C;
  font-size: 12px;
}
.remark-text {
  color: #909399;
  font-size: 13px;
}
</style>
